/**
 * constellationInject — pure helpers for injecting constellation data files into a
 * sketch. No Redux, no DOM: turns catalog data into a self-registering JS file and
 * edits the sketch's `index.html` <script> tags. Browser-safe.
 */

/** "Ursa Major" → "ursa-major" (filename-safe, lowercase, hyphenated). */
export function slug(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The injected filename for a constellation, e.g. "Canis Major" → "canis-major.js". */
export function filenameFor(name) {
  return `${slug(name)}.js`;
}

/** One `"Name": { ra, dec, connectsTo: [...] }` line, no trailing comma. */
function renderStarLine(name, star) {
  const neighbors = star.connectsTo.map((n) => JSON.stringify(n)).join(', ');
  return `  ${JSON.stringify(name)}: { ra: ${star.ra}, dec: ${
    star.dec
  }, connectsTo: [${neighbors}] }`;
}

/**
 * The source of an injected constellation file. Self-registers into the shared
 * `window.constellations` registry so no single file grows large and only the
 * loaded constellations appear.
 *
 * @param {string} name  constellation name (registry key)
 * @param {Object<string, {ra:number, dec:number, connectsTo:string[]}>} stars
 * @returns {string} JavaScript source
 */
export function renderConstellationFile(name, stars) {
  const body = Object.entries(stars)
    .map(([starName, star]) => renderStarLine(starName, star))
    .join(',\n');
  return `// ${name} · RA/Dec in decimal degrees (J2000). Generated by the Stars menu.
// Tip: to avoid drawing a figure line twice, only draw when name < neighbor.
(window.constellations = window.constellations || {})[${JSON.stringify(
    name
  )}] = {
${body}
};
`;
}

const scriptTagFor = (filename) => `<script src="${filename}"></script>`;

/**
 * Ensure `index.html` loads `filename` before `sketch.js`. Idempotent. Falls back
 * to inserting before `</body>` when there is no sketch.js tag.
 */
export function ensureScriptTag(html, filename) {
  const tag = scriptTagFor(filename);
  if (html.includes(tag)) return html;

  const sketchTag = /([ \t]*)<script\s+src=["']sketch\.js["']><\/script>/;
  if (sketchTag.test(html)) {
    return html.replace(
      sketchTag,
      (match, indent) => `${indent}${tag}\n${match}`
    );
  }

  if (html.includes('</body>')) {
    return html.replace('</body>', `    ${tag}\n  </body>`);
  }

  return `${html}\n${tag}`;
}

/** Remove the `<script src="filename">` tag (and its line) from `index.html`. */
export function removeScriptTag(html, filename) {
  const tag = scriptTagFor(filename);
  if (!html.includes(tag)) return html;
  // Drop the whole line (leading whitespace + trailing newline) when present.
  const line = new RegExp(
    `[ \\t]*${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\n?`
  );
  return html.replace(line, '');
}
